import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress, Tooltip } from "@material-ui/core";
import { toast } from "react-toastify";

import {
  getNotificationSettingsAPI,
  updateNotificationSettingsAPI,
} from "../../../../api/userApi";
import MobileIcon from "../../../../assets/svgs/MobileIcon";
import EMailIcon from "../../../../assets/svgs/EMailIcon";
import { useNotificationsStyles } from "./styles";
import { useAppSelector } from "../../../../redux/typedHooks";
import { UserInterface } from "../../../../types/user";
import InfoCircleIcon from "../../../../assets/svgs/InfoCircleIcon";

type NotificationMode = "inApp" | "email" | "disabled";
type NotificationKey =
  | "mentions"
  | "replies"
  | "newFollower"
  | "profileEngagement"
  | "unreadReminder"
  | "dailyFreeDm"
  | "dmQuotaUsed"
  | "newPostFollowed"
  | "creatorCommentEngagement"
  | "dailyEarnings"
  | "weeklyEarnings"
  | "monthlyPerformance"
  | "firstTip"
  | "newTip"
  | "payoutAlert"
  | "trendingCreator"
  | "topPostsDigest"
  | "verificationApproved"
  | "profileCompletion"
  | "firstUpload"
  | "inactiveCreator"
  | "inactiveViewer"
  | "firstTipSent"
  | "fanBadgeUnlocked"
  | "vipStatusActivated"
  | "engagementMilestone"
  | "topFanRewards"
  | "welcomeEmail"
  | "firstLoginBanner"
  | "viewerOnboardingReminder"
  | "creatorFirstResponse"
  | "newDeviceLogin"
  | "passwordChanged"
  | "twoFactorChange"
  | "policyViolation"
  | "dmcaNotice"
  | "accountRestricted"
  | "paymentMethodUpdated"
  | "platformUpdates"
  | "creatorSpotlight"
  | "exclusiveOffers"
  | "trendingCreators"
  | "exclusiveEvents"
  | "newSubscriber"
  | "subscriptionStarted"
  | "subscriptionRenewal"
  | "subscriptionExpired"
  | "creditLow"
  | "creditPurchase"
  | "payoutProcessed"
  | "exclusiveContentUnlock"
  | "limitedOffer"
  | "postComment"
  | "commentReplies"
  | "commentLikes"
  | "newExclusivePost"
  | "requestedVideoUpdates"
  | "videoRequestSend"
  | "videoRequestDeadlineReminder"

interface NotificationSetting {
  key: NotificationKey;
  title: string;
  description: string;
  viewerOnly?: boolean;
  creatorOnly?: boolean;
  tooltip?: string;
}

type NotificationsState = Record<
  NotificationKey,
  {
    inApp: boolean;
    email: boolean;
  }
>;

interface NotificationCategory {
  id: string;
  sectionTitle: string;
  viewerOnly?: boolean;
  creatorOnly?: boolean;
  items: NotificationSetting[];
}

const DEFAULT_NOTIFICATION_STATE: NotificationsState = {
  mentions: { inApp: true, email: true },
  replies: { inApp: true, email: true },
  newFollower: { inApp: true, email: true },
  profileEngagement: { inApp: true, email: true },
  unreadReminder: { inApp: true, email: true },
  dailyFreeDm: { inApp: true, email: true },
  dmQuotaUsed: { inApp: true, email: true },
  newPostFollowed: { inApp: true, email: true },
  creatorCommentEngagement: { inApp: true, email: true },
  dailyEarnings: { inApp: true, email: true },
  weeklyEarnings: { inApp: true, email: true },
  monthlyPerformance: { inApp: true, email: true },
  firstTip: { inApp: true, email: true },
  newTip: { inApp: true, email: true },
  payoutAlert: { inApp: true, email: true },
  trendingCreator: { inApp: true, email: true },
  topPostsDigest: { inApp: true, email: true },
  verificationApproved: { inApp: true, email: true },
  profileCompletion: { inApp: true, email: true },
  firstUpload: { inApp: true, email: true },
  inactiveCreator: { inApp: true, email: true },
  inactiveViewer: { inApp: true, email: true },
  firstTipSent: { inApp: true, email: false },
  fanBadgeUnlocked: { inApp: true, email: false },
  vipStatusActivated: { inApp: true, email: true },
  engagementMilestone: { inApp: true, email: false },
  topFanRewards: { inApp: true, email: true },
  welcomeEmail: { inApp: false, email: true },
  firstLoginBanner: { inApp: true, email: false },
  viewerOnboardingReminder: { inApp: true, email: true },
  creatorFirstResponse: { inApp: true, email: true },
  newDeviceLogin: { inApp: false, email: true },
  passwordChanged: { inApp: false, email: true },
  twoFactorChange: { inApp: false, email: true },
  policyViolation: { inApp: true, email: true },
  dmcaNotice: { inApp: false, email: true },
  accountRestricted: { inApp: false, email: true },
  paymentMethodUpdated: { inApp: false, email: true },
  platformUpdates: { inApp: true, email: true },
  creatorSpotlight: { inApp: true, email: true },
  exclusiveOffers: { inApp: false, email: true },
  trendingCreators: { inApp: true, email: true },
  exclusiveEvents: { inApp: false, email: true },
  newSubscriber: { inApp: true, email: true },
  subscriptionStarted: { inApp: true, email: true },
  subscriptionRenewal: { inApp: true, email: true },
  subscriptionExpired: { inApp: true, email: true },
  creditLow: { inApp: true, email: true },
  creditPurchase: { inApp: true, email: true },
  payoutProcessed: { inApp: true, email: true },
  exclusiveContentUnlock: { inApp: true, email: false },
  videoRequestSend: { inApp: true, email: false },
  videoRequestDeadlineReminder: { inApp: true, email: false },
  requestedVideoUpdates: { inApp: true, email: true },
  limitedOffer: { inApp: true, email: true },
  postComment: { inApp: true, email: true },
  commentReplies: { inApp: true, email: true },
  commentLikes: { inApp: true, email: true },
  newExclusivePost: { inApp: true, email: true },
};

const Notifications: React.FC = () => {
  const classes = useNotificationsStyles();
  const [notifications, setNotifications] = useState<NotificationsState>(
    DEFAULT_NOTIFICATION_STATE
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [, setError] = useState<string | null>(null);
  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const getUserTypeTitle = (user?: any | null) =>
    user?.userType?.title || user?.usertype?.title;

  const isViewer = getUserTypeTitle(user) === "viewer";

  const notificationCategories = useMemo<NotificationCategory[]>(
    () => [
      {
        id: "profile",
        sectionTitle: "Profile",
        creatorOnly: true,
        items: [
          {
            key: "postComment",
            title: "Comments on my post",
            description: "When someone comments on or engages with your posts.",
            creatorOnly: true,
          },
          {
            key: "profileEngagement",
            title: "Post liked or bookmarked",
            description: "When your post is liked or bookmarked.",
            creatorOnly: true,
          },
          {
            key: "newSubscriber",
            title: "New subscriber",
            description: "When someone starts a new subscription to you.",
            creatorOnly: true,
          },
        ],
      },
      {
        id: "interactions",
        sectionTitle: "Interactions",
        viewerOnly: true,
        items: [
          {
            key: "commentReplies",
            title: "Replies on my comments",
            description: "When someone replies to your comments.",
            viewerOnly: true,
          },
          {
            key: "commentLikes",
            title: "Likes on my comments",
            description: "When someone likes your comments.",
            viewerOnly: true,
          },
          {
            key: "creatorCommentEngagement",
            title: "Replies from post creator",
            description: "When the post creator replies to your comment.",
            viewerOnly: true,
          },
          {
            key: "newPostFollowed",
            title: "New post from your creators",
            description: "When a creator you follow publishes a new post.",
            viewerOnly: true,
          },
          {
            key: "newExclusivePost",
            title: "New exclusive post from your creators",
            description: "When a creator you follow publishes an exclusive post.",
            viewerOnly: true,
          },
          {
            key: "requestedVideoUpdates",
            title: "Requested video updates",
            description: "When a requested video has updates.",
            viewerOnly: true,
          },
        ],
      },
      {
        id: "messages",
        sectionTitle: "Messages",
        items: [
          {
            key: "replies",
            title: "Replies to my message",
            description: "When someone replies in your conversation.",
          },
          {
            key: "mentions",
            title: "Mention in a message",
            description: "When you are @mentioned in a message.",
          },
          {
            key: "unreadReminder",
            title: "Unread message reminder (24h)",
            description: "Reminder if you have unread messages.",
          },
        ],
      },
      {
        id: "earningsPerformance",
        sectionTitle: "Earnings & performance",
        creatorOnly: true,
        items: [
          {
            key: "newTip",
            title: "Tip send",
            description: "When you receive a tip.",
            creatorOnly: true,
          },
          {
            key: "videoRequestSend",
            title: "Video request",
            description: "Alerts for custom / paid content and video-style requests.",
            creatorOnly: true,
          },
          {
            key: "videoRequestDeadlineReminder",
            title: "Video request deadline reminder (24h)",
            description: "Reminds creators 24h before a custom video request deadline to ensure timely delivery.",
            creatorOnly: true,
          },
          {
            key: "dailyEarnings",
            title: "Daily earnings summary",
            description: "Daily snapshot of your earnings.",
            creatorOnly: true,
          },
          {
            key: "weeklyEarnings",
            title: "Weekly earnings summary",
            description: "Weekly performance summary.",
            creatorOnly: true,
          },
          {
            key: "monthlyPerformance",
            title: "Monthly performance digest",
            description: "Monthly overview of your performance.",
            creatorOnly: true,
          },
          {
            key: "payoutProcessed",
            title: "Payout processed",
            description: "When a payout has been sent to you.",
            creatorOnly: true,
          },
        ],
      },
      {
        id: "platformDiscovery",
        sectionTitle: "Platform discovery",
        items: [
          {
            key: "platformUpdates",
            title: "New feature launches and updates",
            description: "Product news and new capabilities.",
          },
          {
            key: "topPostsDigest",
            title: "Receive top Sugar Cribs posts",
            description: "Highlights and discovery from the platform.",
          },
        ],
      },
      {
        id: "payments",
        sectionTitle: "Payments",
        viewerOnly: true,
        items: [
          {
            key: "creditLow",
            title: "Low credits",
            description: "Alert when your credit balance is running low.",
          },
        ],
      },
    ],
    []
  );

  const normalizeSettings = (data: any): NotificationsState => {
    const normalized: NotificationsState = { ...DEFAULT_NOTIFICATION_STATE };
    Object.keys(DEFAULT_NOTIFICATION_STATE).forEach(key => {
      const notificationKey = key as NotificationKey;
      const inAppKey = `${notificationKey}InApp`;
      const emailKey = `${notificationKey}Email`;

      normalized[notificationKey] = {
        inApp: Boolean(
          data?.[inAppKey] ??
          data?.[notificationKey]?.inApp ??
          DEFAULT_NOTIFICATION_STATE[notificationKey].inApp
        ),
        email: Boolean(
          data?.[emailKey] ??
          data?.[notificationKey]?.email ??
          DEFAULT_NOTIFICATION_STATE[notificationKey].email
        ),
      };
    });
    return normalized;
  };

  const buildPayload = (state: NotificationsState) => {
    const payload: Record<string, boolean> = {};
    Object.entries(state).forEach(([key, value]) => {
      payload[`${key}InApp`] = value.inApp;
      payload[`${key}Email`] = value.email;
    });
    return payload;
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setInitialLoading(true);
      try {
        const response = await getNotificationSettingsAPI();
        if (response.success) {
          setNotifications(normalizeSettings(response.data));
        } else {
          setError(response.message || "Failed to fetch notification settings");
        }
      } catch (err) {
        setError("Something went wrong while fetching settings");
        console.error(err);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const persistNotificationChanges = async (
    updatedNotifications: NotificationsState,
    previousState: NotificationsState
  ) => {
    setNotifications(updatedNotifications);
    setIsSaving(true);
    try {
      const response = await updateNotificationSettingsAPI(
        buildPayload(updatedNotifications)
      );
      if (response.success) {
        toast.success("Notification settings updated successfully");
      } else {
        throw new Error(
          response.message || "Failed to update notification settings"
        );
      }
    } catch (err) {
      setNotifications(previousState);
      const message =
        err instanceof Error
          ? err.message
          : "Failed to update notification settings";
      setError(message);
      toast.error(message);
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const getNotificationMode = (notificationKey: NotificationKey): NotificationMode => {
    const current = notifications[notificationKey];
    if (current?.inApp) return "inApp";
    if (current?.email) return "email";
    return "disabled";
  };

  const handleNotificationModeChange = async (
    notificationKey: NotificationKey,
    mode: NotificationMode
  ) => {
    setError(null);
    const previousState = notifications;
    const updatedNotifications: NotificationsState = {
      ...notifications,
      [notificationKey]: {
        inApp: mode === "inApp",
        email: mode === "email",
      },
    };
    await persistNotificationChanges(updatedNotifications, previousState);
  };

  const handleToggleKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    callback: () => void
  ) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      callback();
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" padding={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const visibleCategories = notificationCategories.filter(category => {
    if (category.creatorOnly && isViewer) return false;
    if (category.viewerOnly && !isViewer) return false;
    return true;
  });

  return (
    <div className={classes.section}>
      <Typography className={classes.pageTitle}>Notifications</Typography>
      {visibleCategories.map(category => {
        const filteredItems = category.items.filter(item => {
          if (item.viewerOnly && !isViewer) return false;
          if (item.creatorOnly && isViewer) return false;
          return true;
        });
        if (filteredItems.length === 0) {
          return null;
        }
        return (
          <Box key={category.id} className={classes.categoryCard}>
            <Box
              id={`category-panel-${category.id}`}
              className={classes.categoryBody}
            >
              <Typography className={classes.categorySimpleTitle}>
                {category.sectionTitle}
              </Typography>
              <Box className={classes.tableBody}>
                {filteredItems.map(item => (
                  <Box key={item.key} className={classes.tableRow}>
                    <Box className={classes.rowInfo}>
                      <Box className={classes.rowTitleWrapper}>
                        <Typography className={classes.rowTitle}>
                          {item.title}
                        </Typography>
                        {item.tooltip && (
                          <Tooltip title={item.tooltip} placement="top">
                            <span className={classes.tooltipIcon}>
                              <InfoCircleIcon width={16} height={16} />
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                      {/* {item.viewerOnly && (
                        <Typography className={classes.viewerBadge}>
                          Viewers only
                        </Typography>
                      )} */}
                    </Box>
                    <Box className={classes.channelGroup}>
                      <button
                        className={`${classes.channelToggle} ${getNotificationMode(item.key) === "inApp"
                          ? classes.channelActive
                          : ""
                          }`}
                        role="switch"
                        aria-checked={getNotificationMode(item.key) === "inApp"}
                        aria-label={`Set ${item.title} to in-app (In-App) notifications`}
                        onClick={() => handleNotificationModeChange(item.key, "inApp")}
                        onKeyDown={event =>
                          handleToggleKeyDown(event, () =>
                            handleNotificationModeChange(item.key, "inApp")
                          )
                        }
                        disabled={isSaving}
                      >
                        <MobileIcon style={{ marginRight: 8 }} />
                        In-App
                      </button>
                      <button
                        className={`${classes.channelToggle} ${getNotificationMode(item.key) === "email"
                          ? classes.channelActive
                          : ""
                          }`}
                        role="switch"
                        aria-checked={getNotificationMode(item.key) === "email"}
                        aria-label={`Set ${item.title} to email notifications`}
                        onClick={() => handleNotificationModeChange(item.key, "email")}
                        onKeyDown={event =>
                          handleToggleKeyDown(event, () =>
                            handleNotificationModeChange(item.key, "email")
                          )
                        }
                        disabled={isSaving}
                      >
                        <EMailIcon style={{ marginRight: 8 }} />
                        E-Mail
                      </button>
                      <button
                        className={`${classes.channelToggle} ${getNotificationMode(item.key) === "disabled"
                          ? classes.channelActive
                          : ""
                          }`}
                        role="switch"
                        aria-checked={getNotificationMode(item.key) === "disabled"}
                        aria-label={`Disable ${item.title} notifications`}
                        onClick={() =>
                          handleNotificationModeChange(item.key, "disabled")
                        }
                        onKeyDown={event =>
                          handleToggleKeyDown(event, () =>
                            handleNotificationModeChange(item.key, "disabled")
                          )
                        }
                        disabled={isSaving}
                      >
                        Disable
                      </button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );
      })}
    </div>
  );
};

export default Notifications;
